export interface BatchedMessage {
  type: '__BATCH__';
  messages: any[];
}

export interface StatusMessage {
  type: '__STATUS__';
  status: 'CONNECTED' | 'DISCONNECTED' | 'SESSION_EXPIRED';
}

export type SocketMessage = BatchedMessage | StatusMessage | any;

// 1008 = "Policy Violation"; the BE sends this when authentication fails
// (`websocket.close(code=1008, reason="Authentication failed")`).
const WS_AUTH_FAILED_CLOSE_CODE = 1008;
// 1006 = abnormal closure — common after proxy idle timeout with an expired JWT.
const WS_ABNORMAL_CLOSE_CODE = 1006;

class SocketManager {
  ws: WebSocket | null = null;
  url: string | null = null;
  private getProtocol: (() => string | undefined) | null = null;
  private onAuthFailure: (() => Promise<void> | void) | null = null;
  private intentionalClose = false;
  private reconnectTimerId: ReturnType<typeof setTimeout> | null = null;
  // The BE accepts every handshake and only then closes with 1008 on bad auth,
  // so `onopen` proves nothing. It never sends a frame before auth succeeds —
  // the first received frame is the only proof the session is authenticated.
  private authConfirmed = false;
  private authFailures = 0;
  private stopped = false;

  isConnected = false;
  reconnectAttempts = 0;
  maxReconnectAttempts = 10;
  maxAuthFailures = 3;

  baseDelay = 1000;
  maxDelay = 15000;

  heartbeatInterval: NodeJS.Timeout | null = null;
  // Must be well below any reverse-proxy / ingress idle timeout (AGC default
  // is ~5 minutes for WebSockets).
  heartbeatTime = 30000;
  // Allow up to ~2 missed heartbeats before force-closing.
  heartbeatMissAllowance = 2.5;
  lastPong = Date.now();

  messageQueue: any[] = [];
  flushScheduled = false;

  listeners = new Set<(data: SocketMessage) => void>();

  connect(
    url: string,
    getProtocol?: () => string | undefined,
    onAuthFailure?: () => Promise<void> | void,
  ) {
    if (this.stopped) return;

    this.url = url;
    this.authConfirmed = false;
    this.getProtocol = getProtocol ?? null;
    if (onAuthFailure !== undefined) {
      this.onAuthFailure = onAuthFailure;
    }

    this.cancelPendingReconnect();

    if (this.ws) {
      this.intentionalClose = true;
      this.cleanup();
      this.intentionalClose = false;
    }

    // Never pass an empty subprotocol: `new WebSocket(url, '')` throws a
    // synchronous SyntaxError, and inside scheduleReconnect's setTimeout that
    // uncaught throw silently kills the whole reconnect chain.
    const protocol = getProtocol?.() ?? '';
    this.ws = (protocol ? new WebSocket(url, protocol) : new WebSocket(url)) as WebSocket;

    this.ws.onopen = () => {
      this.isConnected = true;
      this.startHeartbeat();
    };

    this.ws.onmessage = (msg: MessageEvent) => {
      const data = JSON.parse(msg.data);

      if (!this.authConfirmed) {
        this.authConfirmed = true;
        this.reconnectAttempts = 0;
        this.authFailures = 0;
        this.listeners.forEach((cb) => cb({ type: '__STATUS__', status: 'CONNECTED' }));
      }

      if (data.type === 'PONG') {
        this.lastPong = Date.now();
        return;
      }

      this.messageQueue.push(data);
      this.scheduleFlush();
    };

    this.ws.onerror = (error: Event) => {
      console.error('WS Error:', error);
    };

    this.ws.onclose = (event: CloseEvent) => {
      console.warn('WS Disconnected', event);
      this.isConnected = false;
      this.ws = null;

      this.listeners.forEach((cb) => cb({ type: '__STATUS__', status: 'DISCONNECTED' }));

      this.stopHeartbeat();

      if (this.intentionalClose) {
        return;
      }

      // A 1006 on a socket that did authenticate is a network/proxy drop, not a
      // token problem — only spend the auth budget when no frame ever arrived.
      if (
        event.code === WS_AUTH_FAILED_CLOSE_CODE ||
        (event.code === WS_ABNORMAL_CLOSE_CODE &&
          !this.authConfirmed &&
          this.reconnectAttempts >= 2)
      ) {
        this.handleAuthFailureReconnect();
        return;
      }

      this.scheduleReconnect();
    };
  }

  private async handleAuthFailureReconnect() {
    if (this.stopped) return;

    this.authFailures++;
    if (this.authFailures >= this.maxAuthFailures) {
      this.stop();
      return;
    }

    if (this.onAuthFailure && this.url) {
      try {
        await this.onAuthFailure();
      } catch (err) {
        console.error('Auth refresh failed before WS reconnect:', err);
      }
    }

    this.scheduleReconnect();
  }

  /** Terminal state: stop retrying until a genuine token rotation re-arms us. */
  private stop() {
    this.stopped = true;
    this.stopHeartbeat();
    this.cleanup();
    this.listeners.forEach((cb) => cb({ type: '__STATUS__', status: 'SESSION_EXPIRED' }));
  }

  cancelPendingReconnect() {
    if (this.reconnectTimerId) {
      clearTimeout(this.reconnectTimerId);
      this.reconnectTimerId = null;
    }
  }

  scheduleFlush() {
    if (this.flushScheduled) return;
    this.flushScheduled = true;

    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => this.flushMessages());
    } else {
      setTimeout(() => this.flushMessages(), 16);
    }
  }

  flushMessages() {
    this.flushScheduled = false;

    if (this.messageQueue.length === 0) return;

    const messages = this.messageQueue;
    this.messageQueue = [];

    const batchMessage: BatchedMessage = { type: '__BATCH__', messages };
    this.listeners.forEach((cb) => cb(batchMessage));
  }

  send(data: any) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
      return true;
    } else {
      console.warn('Socket not ready. Dropped:', data);
      return false;
    }
  }

  isSocketOpen(): boolean {
    return this.isConnected && this.ws?.readyState === WebSocket.OPEN;
  }

  /** True when the socket is open, authenticated, and heartbeats are being answered. */
  isSocketHealthy(): boolean {
    if (!this.isSocketOpen() || !this.authConfirmed) return false;
    const staleThreshold = this.heartbeatTime * this.heartbeatMissAllowance;
    return Date.now() - this.lastPong <= staleThreshold;
  }

  scheduleReconnect() {
    if (this.stopped) return;

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnect attempts reached');
      this.stop();
      return;
    }

    this.cancelPendingReconnect();

    // Jitter spreads retries so a fleet of tabs doesn't stampede the BE.
    const delay =
      Math.min(this.baseDelay * Math.pow(2, this.reconnectAttempts), this.maxDelay) *
      (0.5 + Math.random());

    this.reconnectTimerId = setTimeout(() => {
      this.reconnectTimerId = null;
      this.reconnectAttempts++;
      if (this.url) {
        this.connect(
          this.url,
          this.getProtocol ?? undefined,
          this.onAuthFailure ?? undefined,
        );
      }
    }, delay);
  }

  resetReconnectAttempts() {
    this.reconnectAttempts = 0;
    this.authFailures = 0;
    this.stopped = false;
    this.cancelPendingReconnect();
  }

  startHeartbeat() {
    this.lastPong = Date.now();
    // Ping immediately: the reply is what confirms auth, so waiting a full
    // interval would leave the UI unable to tell a live socket from a doomed one.
    this.send({ type: 'PING' });

    this.heartbeatInterval = setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

      this.send({ type: 'PING' });

      if (Date.now() - this.lastPong > this.heartbeatTime * this.heartbeatMissAllowance) {
        console.warn('Server missed pong. Closing connection.');
        this.ws.close();
      }
    }, this.heartbeatTime);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);
  }

  cleanup() {
    this.cancelPendingReconnect();
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onclose = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.close();
      this.ws = null;
    }
    this.isConnected = false;
    this.authConfirmed = false;
    this.stopHeartbeat();
    this.messageQueue = [];
    this.flushScheduled = false;
  }

  subscribe(callback: (data: SocketMessage) => void) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }
}

export const socketManager = new SocketManager();
